import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { Megaphone, Activity, Shield, LogOut, Menu, X, Lock, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import StaffCampaigns from '../components/staff/StaffCampaigns';
import StaffActivityLogs from '../components/staff/StaffActivityLogs';
import StaffTrustedDevices from '../components/staff/StaffTrustedDevices';
import StaffChangePassword from '../components/staff/StaffChangePassword';

const StaffDashboard = ({ onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    // Check session every 30 seconds
    const sessionCheck = setInterval(async () => {
      try {
        await api.checkSession();
      } catch (error) {
        toast.error('Session expired');
        handleLogout();
      }
    }, 30000);

    // Inactivity detection
    let inactivityTimeout;
    let warningTimeout;

    const resetTimer = () => {
      clearTimeout(inactivityTimeout);
      clearTimeout(warningTimeout);
      setShowInactivityWarning(false);
      setCountdown(30);

      // Show warning after 9 minutes 30 seconds
      warningTimeout = setTimeout(() => {
        setShowInactivityWarning(true);
        let counter = 30;
        const countdownInterval = setInterval(() => {
          counter--;
          setCountdown(counter);
          if (counter <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);
      }, 9.5 * 60 * 1000);

      // Auto logout after 10 minutes
      inactivityTimeout = setTimeout(() => {
        toast.info('Logged out due to inactivity');
        handleLogout();
      }, 10 * 60 * 1000);
    };

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      clearInterval(sessionCheck);
      clearTimeout(inactivityTimeout);
      clearTimeout(warningTimeout);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      onLogout();
    }
  };

  const handleStayActive = () => {
    setShowInactivityWarning(false);
    // Trigger activity to reset timer
    document.dispatchEvent(new Event('mousedown'));
  };

  const menuItems = [
    { path: '/staff', label: 'Campaigns', icon: Megaphone },
    { path: '/staff/activity', label: 'Activity Logs', icon: Activity },
    { path: '/staff/devices', label: 'Trusted Devices', icon: Shield },
    { path: '/staff/password', label: 'Change Password', icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900" data-testid="staff-dashboard-title">
                Global CRM Software - Staff
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900" data-testid="staff-name">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-2 mt-16 lg:mt-0" data-testid="staff-nav">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors
                    ${isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<StaffCampaigns />} />
            <Route path="activity" element={<StaffActivityLogs />} />
            <Route path="devices" element={<StaffTrustedDevices />} />
            <Route path="password" element={<StaffChangePassword />} />
          </Routes>
        </main>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Inactivity Warning Dialog */}
      <Dialog open={showInactivityWarning} onOpenChange={setShowInactivityWarning}>
        <DialogContent data-testid="inactivity-warning-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              <DialogTitle>Inactivity Warning</DialogTitle>
            </div>
            <DialogDescription>
              You will be logged out in <span className="font-bold text-orange-600" data-testid="countdown-timer">{countdown}</span> seconds due to inactivity.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button onClick={handleStayActive} className="flex-1" data-testid="stay-active-btn">
              Stay Active
            </Button>
            <Button variant="outline" onClick={handleLogout} className="flex-1" data-testid="logout-now-btn">
              Logout Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffDashboard;
